import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Modal,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Chip,
  Checkbox,
  Portal,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { toast } from 'react-hot-toast';
import { axiosInstance } from '../../../utils/tokenUtils';
import { useTranslation } from '../../../i18n/LanguageContext';
import BookIcon from '../../../assets/MyAccountIcon/book.svg';
import './RequestNewSubjectModal.css';

const TagCard = ({ tag, isSelected, onSelect }) => {
  return (
    <Box onClick={() => onSelect(tag)} className={`tag-card ${isSelected ? 'selected' : ''}`}>
      {tag.title || tag.name}
    </Box>
  );
};

const RequestNewSubjectModal = ({
  open,
  onClose,
  subjects,
  onSubjectsEmpty,
  onSuccess,
  canRequestSubjects,
}) => {
  const { translate } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [isNewSubject, setIsNewSubject] = useState(false);
  const inputRef = useRef(null);

  // State for subjects with pagination
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // State for tag suggestions
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);

  // State for tags with pagination
  const [availableTags, setAvailableTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isLoadingMoreTags, setIsLoadingMoreTags] = useState(false);
  const [hasMoreTags, setHasMoreTags] = useState(true);
  const [currentTagOffset, setCurrentTagOffset] = useState(0);
  const [totalTagCount, setTotalTagCount] = useState(0);

  // State to store tag objects (with IDs) for submission
  const [selectedTagObjects, setSelectedTagObjects] = useState([]);

  // Add these new refs and effects near the top of the component:
  const tagInputRef = useRef(null);
  const tagDropdownRef = useRef(null);
  const dropdownRef = useRef(null);

  // Add this new effect to handle dropdown positioning
  useEffect(() => {
    if (showTagSuggestions && tagInputRef.current && tagDropdownRef.current) {
      const inputRect = tagInputRef.current.getBoundingClientRect();
      const dropdownEl = tagDropdownRef.current;

      dropdownEl.style.top = `${inputRect.bottom + 4}px`;
      dropdownEl.style.left = `${inputRect.left}px`;
    }
  }, [showTagSuggestions, tagSuggestions]);

  // Check if subjects array is empty and try to reload once
  useEffect(() => {
    if (open && subjects && subjects.length === 0 && onSubjectsEmpty) {
      setIsLoading(true);
      // Call the callback to reload subjects
      const loadSubjects = async () => {
        try {
          await onSubjectsEmpty();
        } catch (error) {
          console.error('Failed to reload subjects:', error);
        } finally {
          setIsLoading(false);
        }
      };

      loadSubjects();
    } else if (open) {
      setIsLoading(false);
    }
  }, [open, subjects, onSubjectsEmpty]);

  useEffect(() => {
    if (open) {
      setInputValue('');
      setTagInput('');
      setSelectedTags([]);
      setSelectedSubject(null);
      setIsNewSubject(false);
      setTagSuggestions([]);
      setShowTagSuggestions(false);
      // Make sure dropdown is hidden on open
      setShowSuggestions(false);
    }
  }, [open, subjects]);

  useEffect(() => {
    if (inputValue.trim() === '') {
      // When input is empty, show all subjects instead of clearing
      setFilteredSubjects(availableSubjects);
      // Only hide suggestions if dropdown is not explicitly shown
      if (!showSuggestions) {
        setShowSuggestions(false);
      }
      setIsNewSubject(false);
      return;
    }

    // Filter subjects by title (using the full subject objects)
    const filtered = availableSubjects.filter(subject =>
      subject.title.toLowerCase().includes(inputValue.toLowerCase())
    );

    setFilteredSubjects(filtered);
    // Don't automatically show suggestions when typing - only if dropdown is already open
    if (showSuggestions) {
      setShowSuggestions(filtered.length > 0);
    }
    setIsNewSubject(filtered.length === 0);
  }, [inputValue, availableSubjects, showSuggestions]);

  const handleInputChange = e => {
    setInputValue(e.target.value);
    setSelectedSubject(null);
    // Only show suggestions when dropdown is already open
    if (showSuggestions) {
      setShowSuggestions(true);
    }

    // Clear tags when subject input changes
    setSelectedTags([]);
    setSelectedTagObjects([]);
    setTagInput('');
  };

  const handleSelectSubject = async subject => {
    // If a new subject is selected or the subject changes, clear all the tags
    if (!selectedSubject || selectedSubject.id !== subject.id) {
      setSelectedTags([]);
      setSelectedTagObjects([]);
      setTagInput('');

      // Reset tag pagination state
      setCurrentTagOffset(0);
      setHasMoreTags(true);
      setTotalTagCount(0);
    }

    setSelectedSubject(subject);
    setInputValue(subject.title);
    setShowSuggestions(false);
    setIsNewSubject(false);
    setTagSuggestions([]);

    // Fetch tags when subject is selected
    await fetchAllTags(subject.id, 0, false);
  };

  // Toggle dropdown visibility when clicking the input field
  const toggleSuggestions = () => {
    setShowSuggestions(!showSuggestions);
    // When opening dropdown, ensure we show all subjects if input is empty
    if (!showSuggestions && inputValue.trim() === '') {
      setFilteredSubjects(availableSubjects);
    }
  };

  // New function to fetch tag suggestions based on input and subject ID
  const fetchTagSuggestions = async (subjectId, query) => {
    if (!subjectId || !query) {
      setTagSuggestions([]);
      setShowTagSuggestions(false);
      return;
    }

    try {
      setLoadingTags(true);
      console.log('Fetching tag suggestions for subject ID:', subjectId, 'with query:', query);

      const response = await axiosInstance.get(`/common/${subjectId}/tags`, {
        params: { query },
      });

      console.log('Tag suggestions API response:', response.data);

      // Handle the new paginated response structure
      let tagsData = [];

      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        // New paginated structure: { count, offset, limit, results: [...] }
        tagsData = response.data.results;
      } else if (Array.isArray(response.data)) {
        // Fallback: If response.data is directly an array of tags
        tagsData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Fallback: If response.data has a nested data property with the array
        tagsData = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        // Fallback: Try to extract tags if it's an object with specific properties
        tagsData = Object.values(response.data).filter(
          item => item && typeof item === 'object' && (item.id || item.name || item.title)
        );
      }

      console.log('Processed tag suggestions:', tagsData);

      // Store the tag suggestions and always show dropdown
      setTagSuggestions(tagsData);
      setShowTagSuggestions(true);
    } catch (error) {
      console.error('Error fetching tag suggestions:', error);
      setTagSuggestions([]);
      setShowTagSuggestions(true); // Still show dropdown to display "No similar tag available"
    } finally {
      setLoadingTags(false);
    }
  };

  // Log tag suggestions state for debugging
  useEffect(() => {
    console.log('Current tag suggestions state:', tagSuggestions);
    console.log('Show tag suggestions:', showTagSuggestions);
  }, [tagSuggestions, showTagSuggestions]);

  // Updated function to handle tag input changes
  const handleTagInputChange = e => {
    const value = e.target.value;
    setTagInput(value);

    // Fetch suggestions if selected subject exists and current tag has at least 1 character
    if (selectedSubject && selectedSubject.id && value.length >= 1) {
      // Debounce the API call to avoid too many requests
      clearTimeout(window.tagSuggestionsTimer);
      window.tagSuggestionsTimer = setTimeout(() => {
        fetchTagSuggestions(selectedSubject.id, value);
      }, 300);
    } else if (value.length === 0) {
      setTagSuggestions([]);
      setShowTagSuggestions(false);
    }
  };

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target) &&
        tagInputRef.current &&
        !tagInputRef.current.contains(event.target)
      ) {
        setShowTagSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update the focus handler for tag input
  const handleTagInputFocus = () => {
    if (tagInput.length >= 1) {
      setShowTagSuggestions(true);
    }
  };

  // New function to handle selecting a tag suggestion
  const handleSelectTagSuggestion = (tagName, tagObject) => {
    if (!selectedTags.includes(tagName)) {
      setSelectedTags([...selectedTags, tagName]);

      // Store the tag object with ID if it exists
      if (tagObject && tagObject.id) {
        setSelectedTagObjects(prev => [...prev, tagObject]);
      }
    }

    // Clear the input field and suggestions
    setTagInput('');
    setShowTagSuggestions(false);
  };

  // Handle removing a tag
  const handleRemoveTag = (tagToRemove, index) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
    // Also remove from tag objects array using the index
    setSelectedTagObjects(prev => prev.filter((_, i) => i !== index));
  };

  const handleTagInputKeyPress = e => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!selectedTags.includes(tagInput.trim())) {
        setSelectedTags([...selectedTags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const handleSubmit = async () => {
    if (!inputValue.trim()) {
      toast.error('Please enter a subject name');
      return;
    }

    try {
      // Get tag IDs for submission
      const tagIds = selectedTagObjects
        .filter(tagObj => tagObj && tagObj.id)
        .map(tagObj => tagObj.id);

      // When availableSubjects is empty or the subject is not found, create a new subject
      if (availableSubjects.length === 0 || isNewSubject) {
        // For completely new subjects
        await axiosInstance.post('/teachers/request-subject', {
          subject: inputValue,
          tags: selectedTags, // Use tag names for brand new subjects
        });

        toast.success('Your request to add a new subject has been submitted for review');
      } else if (selectedSubject) {
        // For existing subjects, use the new endpoint with proper format
        await axiosInstance.post('/teachers/subjects/request', {
          subject: selectedSubject.id, // Send subject ID
          tags: tagIds.length ? tagIds : selectedTags.map(tag => tag), // Use tag IDs when available
        });

        toast.success(`${selectedSubject.title} has been sent for approval`);
      }

      // Call the onSuccess callback to refresh data in parent components if provided
      if (typeof onSuccess === 'function') {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error submitting subject:', error);
      if (error.response) {
        console.error('Server response error:', error.response.data);
        toast.error(error.response.data?.detail || 'Failed to submit subject request');
      } else {
        toast.error('Failed to submit subject request. Please try again.');
      }
    }
  };

  // Function to fetch subjects with pagination
  const fetchSubjects = async (offset = 0, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      // Use the endpoint to fetch subjects not in the teacher's profile with pagination
      const response = await axiosInstance.get('/teachers/subjects/not-in-profile', {
        params: {
          offset: offset,
          limit: 30,
        },
      });

      console.log('Subjects API response:', response.data);

      // Extract data from the new API structure
      const { count, results } = response.data;

      if (results && Array.isArray(results)) {
        // Ensure all subjects have required properties
        const validSubjects = results.filter(
          subject => subject && typeof subject === 'object' && subject.id && subject.title
        );

        if (isLoadMore) {
          // Merge with existing subjects
          setAvailableSubjects(prev => [...prev, ...validSubjects]);
          setFilteredSubjects(prev => [...prev, ...validSubjects]);
        } else {
          // Replace existing subjects
          setAvailableSubjects(validSubjects);
          setFilteredSubjects(validSubjects);
        }

        setTotalCount(count);
        setCurrentOffset(offset + validSubjects.length);
        setHasMore(offset + validSubjects.length < count);
      } else {
        console.warn('No valid subjects found in API response');
        if (!isLoadMore) {
          setAvailableSubjects([]);
          setFilteredSubjects([]);
          toast.info('No subjects available to add');
        }
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      if (error.response) {
        console.error('Server error response:', error.response.data);
      }

      if (!isLoadMore) {
        setAvailableSubjects([]);
        setFilteredSubjects([]);
        toast.error('Failed to load subjects. Please try again.');
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      setIsInitialLoad(false);
    }
  };

  // Function to load more subjects
  const loadMoreSubjects = () => {
    if (!isLoadingMore && hasMore) {
      fetchSubjects(currentOffset, true);
    }
  };

  // Handle scroll event for infinite scroll
  const handleDropdownScroll = event => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;

    // Load more when user scrolls to 80% of the dropdown
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      loadMoreSubjects();
    }
  };

  // Fetch subjects when modal opens
  useEffect(() => {
    if (open) {
      // Reset pagination state
      setCurrentOffset(0);
      setHasMore(true);
      setTotalCount(0);
      setIsInitialLoad(true);
      fetchSubjects(0, false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setInputValue('');
      setTagInput('');
      setSelectedTags([]);
      setSelectedSubject(null);
      setIsNewSubject(false);
      setTagSuggestions([]);
      setShowTagSuggestions(false);
      // Make sure dropdown is hidden initially
      setShowSuggestions(false);
    }
  }, [open]);

  useEffect(() => {
    if (inputValue.trim() === '') {
      // When input is empty, show all subjects instead of clearing
      setFilteredSubjects(availableSubjects);
      // Don't automatically show suggestions
      if (!showSuggestions) {
        setShowSuggestions(false);
      }
      setIsNewSubject(false);
      return;
    }

    // Filter subjects by title (using the full subject objects)
    const filtered = availableSubjects.filter(subject =>
      subject.title.toLowerCase().includes(inputValue.toLowerCase())
    );

    setFilteredSubjects(filtered);
    // Only show suggestions if dropdown is already open
    if (showSuggestions) {
      setShowSuggestions(filtered.length > 0);
    }
    setIsNewSubject(filtered.length === 0);
  }, [inputValue, availableSubjects, showSuggestions]);

  // Function to retry fetching subjects if it failed initially
  const handleRetryFetchSubjects = () => {
    fetchSubjects(0, false);
  };

  // New function to fetch all tags for a subject with pagination
  const fetchAllTags = async (subjectId, offset = 0, isLoadMore = false) => {
    if (!subjectId) return;

    try {
      if (isLoadMore) {
        setIsLoadingMoreTags(true);
      } else {
        setIsLoadingTags(true);
      }

      const response = await axiosInstance.get(`/common/${subjectId}/tags`, {
        params: {
          offset: offset,
          limit: 20,
        },
      });

      console.log('Tags API response:', response.data);

      let tagsData = [];

      // Handle the new paginated response structure
      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        // New paginated structure: { count, offset, limit, results: [...] }
        tagsData = response.data.results;
      } else if (Array.isArray(response.data)) {
        // Fallback: If response.data is directly an array of tags
        tagsData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Fallback: If response.data has a nested data property with the array
        tagsData = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        // Fallback: Try to extract tags if it's an object with specific properties
        tagsData = Object.values(response.data).filter(
          item => item && typeof item === 'object' && (item.id || item.name || item.title)
        );
      }

      if (isLoadMore) {
        // Merge with existing tags
        setAvailableTags(prev => [...prev, ...tagsData]);
      } else {
        // Replace existing tags
        setAvailableTags(tagsData);
      }

      // Update pagination state
      if (response.data && response.data.count !== undefined) {
        setTotalTagCount(response.data.count);
        setCurrentTagOffset(offset + tagsData.length);
        setHasMoreTags(offset + tagsData.length < response.data.count);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      if (!isLoadMore) {
        setAvailableTags([]);
      }
    } finally {
      setIsLoadingTags(false);
      setIsLoadingMoreTags(false);
    }
  };

  // Function to load more tags
  const loadMoreTags = () => {
    if (!isLoadingMoreTags && hasMoreTags && selectedSubject) {
      fetchAllTags(selectedSubject.id, currentTagOffset, true);
    }
  };

  const handleTagSelect = tag => {
    const tagName = tag.title || tag.name;

    if (selectedTags.includes(tagName)) {
      // Remove tag if already selected
      setSelectedTags(selectedTags.filter(t => t !== tagName));
      setSelectedTagObjects(prev => prev.filter(t => t.id !== tag.id));
    } else {
      // Add tag if not selected
      setSelectedTags([...selectedTags, tagName]);
      setSelectedTagObjects([...selectedTagObjects, tag]);
    }
  };

  // Replace the Tags Input section with the new card-based UI
  const renderTagsSection = () => (
    <Box className="tag-section">
      <Typography className="subject-modal-title">Tags</Typography>

      {isLoadingTags ? (
        <Box className="tag-section-loading">
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {!selectedSubject ? (
            <Typography className="subject-modal-description">
              Select a subject first to view available tags
            </Typography>
          ) : (
            <Box
              className="tag-grid"
              sx={{
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '8px',
              }}
              onScroll={event => {
                const { scrollTop, scrollHeight, clientHeight } = event.target;
                // Load more when user scrolls to 80% of the tag grid
                if (scrollTop + clientHeight >= scrollHeight * 0.8) {
                  loadMoreTags();
                }
              }}
            >
              {availableTags.map((tag, index) => (
                <TagCard
                  key={`${tag.id}-${index}`}
                  tag={tag}
                  isSelected={selectedTags.includes(tag.title || tag.name)}
                  onSelect={() => handleTagSelect(tag)}
                />
              ))}

              {/* Loading indicator for infinite scroll */}
              {isLoadingMoreTags && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '16px',
                    width: '100%',
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1, fontSize: '12px' }}>
                    Loading more tags...
                  </Typography>
                </Box>
              )}

              {/* End of list indicator */}
              {/* {!hasMoreTags && availableTags.length > 0 && (
                <Box
                  sx={{
                    textAlign: 'center',
                    padding: '8px 16px',
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    marginTop: '8px',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                    No more tags to load
                  </Typography>
                </Box>
              )} */}

              {availableTags.length === 0 && !isLoadingMoreTags && (
                <Typography className="subject-modal-description">
                  No tags available for this subject
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="request-subject-modal-title">
      <Box className="subject-modal-container">
        {/* Header section */}
        <Box className="subject-modal-header">
          <Typography className="model-title">Add a New Subject</Typography>
          <IconButton onClick={onClose} className="subject-modal-close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Modal content */}
        <Box className="subject-modal-content">
          {/* Icon */}
          <Box className="subject-modal-icon-container">
            <img src={BookIcon} alt="Book" className="subject-modal-icon" />
          </Box>
          <Box className="subject-modal-content-container">
            {/* Show warning message when canRequestSubjects is false */}
            {!canRequestSubjects && (
              <Box
                sx={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#FFF8E0',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  border: '1px solid #F2994A',
                }}
              >
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#F2994A',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: 'Poppins',
                    flex: 1,
                  }}
                >
                  New subject request can only be made once every 7 days. Please try again later.
                </Typography>
              </Box>
            )}

            {/* Title */}
            <Typography variant="h6" component="h2" className="subject-modal-title">
              Add a New Subject
            </Typography>

            {/* Description */}
            <Typography variant="body2" className="subject-modal-description">
              Your request to add more Subject will be sent to admin for review. You will receive
              the updates in your Notifications.
            </Typography>

            {isLoading && isInitialLoad ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  height: '150px',
                  width: '100%',
                }}
              >
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading available subjects...
                </Typography>
              </Box>
            ) : availableSubjects.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  height: '150px',
                  width: '100%',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No subjects are available to add to your profile.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  You can request a new subject by entering the name below.
                </Typography>

                {/* Add minimal subject input for new subject request */}
                <Box sx={{ width: '100%', mt: 3 }}>
                  <TextField
                    fullWidth
                    placeholder="Enter new subject name"
                    value={inputValue}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="medium"
                    sx={{ mb: 2 }}
                  />
                  {inputValue.trim().length > 0 && (
                    <>
                      <TextField
                        fullWidth
                        placeholder="Enter tags (press Enter to add)"
                        value={tagInput}
                        onChange={handleTagInputChange}
                        onKeyPress={handleTagInputKeyPress}
                        variant="outlined"
                        size="medium"
                      />

                      {/* Display selected tags */}
                      {selectedTags.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                          {selectedTags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              onDelete={() => handleRemoveTag(tag, index)}
                              className="tag-chip"
                              sx={{
                                backgroundColor: '#e2efff',
                                color: '#0a5ef3',
                                borderRadius: '30px',
                                '& .MuiChip-deleteIcon': {
                                  color: '#0a5ef3',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            ) : (
              <>
                {/* Subject Input */}
                <Box className="subject-input-container">
                  <Typography className="subject-modal-title">Subject</Typography>
                  <TextField
                    fullWidth
                    placeholder="Select or type to search"
                    value={inputValue}
                    onChange={handleInputChange}
                    onClick={toggleSuggestions}
                    inputRef={inputRef}
                    InputProps={{
                      endAdornment: inputValue ? (
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation(); // Prevent triggering the field's onClick
                            setInputValue('');
                            setSelectedSubject(null);
                            setIsNewSubject(false);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation(); // Prevent triggering the field's onClick
                            toggleSuggestions();
                          }}
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      ),
                    }}
                    className="subject-select-input"
                  />

                  {/* Dropdown suggestions with infinite scroll */}
                  {showSuggestions && (
                    <Box
                      ref={dropdownRef}
                      className="subject-dropdown"
                      sx={{
                        position: 'absolute',
                        left: 0,
                        width: '100%',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        backgroundColor: '#fff',
                        border: '1px solid #d5dee3',
                        borderRadius: '8px',
                        zIndex: 9999,
                        marginTop: '4px',
                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
                      }}
                      onScroll={handleDropdownScroll}
                    >
                      {filteredSubjects.length > 0 ? (
                        <>
                          <List dense sx={{ padding: '8px 0' }}>
                            {filteredSubjects.map((subject, index) => (
                              <ListItem
                                key={`${subject.id}-${index}`}
                                button
                                onClick={() => handleSelectSubject(subject)}
                                sx={{
                                  padding: '8px 16px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  '&:hover': {
                                    backgroundColor: '#f5f7f9',
                                  },
                                  backgroundColor:
                                    selectedSubject && selectedSubject.id === subject.id
                                      ? '#f5f7f9'
                                      : 'transparent',
                                }}
                              >
                                <ListItemText
                                  primary={subject.title}
                                  sx={{
                                    '& .MuiTypography-root': {
                                      fontFamily: 'Poppins',
                                      fontSize: '14px',
                                    },
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>

                          {/* Loading indicator for infinite scroll */}
                          {isLoadingMore && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '16px',
                              }}
                            >
                              <CircularProgress size={20} />
                              <Typography variant="body2" sx={{ ml: 1, fontSize: '12px' }}>
                                Loading more subjects...
                              </Typography>
                            </Box>
                          )}

                          {/* End of list indicator */}
                          {/* {!hasMore && filteredSubjects.length > 0 && (
                            <Box
                              sx={{
                                textAlign: 'center',
                                padding: '8px 16px',
                                borderTop: '1px solid #e0e0e0',
                                backgroundColor: '#f9f9f9',
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: '12px' }}
                              >
                                No more subjects to load
                              </Typography>
                            </Box>
                          )} */}
                        </>
                      ) : (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No subjects available. Please add a new subject.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Tags Input */}
                {renderTagsSection()}
              </>
            )}
          </Box>
        </Box>
        {/* Buttons - now directly inside the container, after the content */}
        <Box className="subject-modal-buttons">
          <Button
            variant="outlined"
            onClick={onClose}
            className="cancel-button-request-new-subject"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            className={`confirm-button-request-new-subject ${!canRequestSubjects ? 'subject-request-disabled' : ''}`}
            disabled={
              isLoading ||
              (availableSubjects.length > 0 && !isNewSubject && !selectedSubject) ||
              inputValue.trim() === '' ||
              !canRequestSubjects
            }
          >
            {!canRequestSubjects ? 'Request Limit Reached' : 'Confirm'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RequestNewSubjectModal;
